import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, TrendingDown, TrendingUp } from 'lucide-react';
import {
  calculateColliOverhead,
  calculateRecipeCost,
  calculateVPELines,
} from '@growthdept/blocks/gift-pricing';
import { updatePackage } from '@/lib/api';
import type { GiftPackage } from '@/lib/types';
import { PricingModelConversion } from './PricingModelConversion';
import { calculateGiftPackagePricing } from '@/lib/packagePricing';
import { PricingComponentCard } from './PricingComponentCard';

interface Props {
  pkg: GiftPackage;
  onChanged: () => void;
}

export function BudgetPanel({ pkg, onChanged }: Props) {
  return (
    <div className="space-y-4 text-sm">
      <PricingModelConversion pkg={pkg} onChanged={onChanged} />
      {pkg.pricing_model_version === 'component_margin_v2' ? (
        <V2BudgetPanel pkg={pkg} onChanged={onChanged} />
      ) : (
        <LegacyBudgetPanel pkg={pkg} onChanged={onChanged} />
      )}
    </div>
  );
}

function LegacyBudgetPanel({ pkg, onChanged }: Props) {
  const [handling, setHandling] = useState(pkg.handling_cost_per_package);
  const [transport, setTransport] = useState(pkg.transport_cost_per_package);
  const [marginLow, setMarginLow] = useState(pkg.margin_low_pct ?? pkg.margin_pct ?? 30);
  const [marginHigh, setMarginHigh] = useState(pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
  const [saving, setSaving] = useState(false);

  // Sync wanneer ander pakket geladen wordt
  useEffect(() => {
    setHandling(pkg.handling_cost_per_package);
    setTransport(pkg.transport_cost_per_package);
    setMarginLow(pkg.margin_low_pct ?? pkg.margin_pct ?? 30);
    setMarginHigh(pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
  }, [pkg.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const vpeLines = useMemo(
    () => calculateVPELines((pkg.items ?? []) as never, pkg.quantity),
    [pkg.items, pkg.quantity]
  );

  const pricing = useMemo(
    () =>
      calculateGiftPackagePricing({
        ...pkg,
        pricing_model_version: 'legacy_v1',
        handling_cost_per_package: handling,
        transport_cost_per_package: transport,
        margin_low_pct: marginLow,
        margin_high_pct: marginHigh,
      }),
    [pkg, handling, transport, marginLow, marginHigh]
  );
  const legacy = pricing.legacy_breakdown!;
  const effectiveBtwRate =
    pricing.total.selling_excl_btw > 0
      ? (pricing.total.btw_euros / pricing.total.selling_excl_btw) * 100
      : 0;

  // Debounced save naar DB. We schrijven ook margin_pct als gewogen gemiddelde
  // zodat oude UI-componenten en queries die alleen margin_pct lezen niet breken.
  useEffect(() => {
    const weightedAvg = Math.round(pricing.total.weighted_margin_pct * 100) / 100;
    const changed =
      handling !== pkg.handling_cost_per_package ||
      transport !== pkg.transport_cost_per_package ||
      marginLow !== (pkg.margin_low_pct ?? pkg.margin_pct ?? 30) ||
      marginHigh !== (pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
    if (!changed) return;
    setSaving(true);
    const t = setTimeout(() => {
      updatePackage(pkg.id, {
        handling_cost_per_package: handling,
        transport_cost_per_package: transport,
        margin_low_pct: marginLow,
        margin_high_pct: marginHigh,
        margin_pct: weightedAvg,
      })
        .then(() => onChanged())
        .finally(() => setSaving(false));
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handling, transport, marginLow, marginHigh, pricing.total.weighted_margin_pct]);

  const recipeCost = useMemo(() => calculateRecipeCost((pkg.items ?? []) as never), [pkg.items]);

  const overhead = useMemo(
    () => calculateColliOverhead((pkg.items ?? []) as never, vpeLines, pkg.quantity),
    [pkg.items, vpeLines, pkg.quantity]
  );

  // Klant-budget vergelijking (incl btw)
  const customerBudgetPerPkgIncl =
    pkg.budget_type === 'incl_btw'
      ? pkg.budget_per_package
      : pkg.budget_per_package * (1 + (effectiveBtwRate || 21) / 100);
  const customerBudgetTotalIncl = customerBudgetPerPkgIncl * pkg.quantity;
  const sellingPerPkgIncl = pkg.quantity > 0 ? pricing.total.selling_incl_btw / pkg.quantity : 0;
  const overUnder = pricing.total.selling_incl_btw - customerBudgetTotalIncl;
  const overUnderPerPkg = sellingPerPkgIncl - customerBudgetPerPkgIncl;

  // Ruimte onder budget (per pakket). "Na marge" = verkoopruimte incl. BTW.
  // "Excl. marge" = inkoopprijs-target: ruimte terugrekenen via marge + BTW, zodat Mark
  // weet welke inkoopprijs hij moet zoeken om het budget precies te vullen. Marge en BTW
  // verschillen per categorie, dus aparte target voor food (9%) en non-food (21%).
  const budgetRoomInclPerPkg =
    pkg.budget_per_package > 0 ? Math.max(0, customerBudgetPerPkgIncl - sellingPerPkgIncl) : 0;
  const purchaseTargetFood = (budgetRoomInclPerPkg * (1 - marginLow / 100)) / 1.09;
  const purchaseTargetNonFood = (budgetRoomInclPerPkg * (1 - marginHigh / 100)) / 1.21;

  const hasLow = legacy.low.purchase_cost > 0;
  const hasHigh = legacy.high.purchase_cost > 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-baseline justify-between text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
          <span>Klant-budget</span>
          {saving && <span className="text-[10px] normal-case text-bmade-400">opslaan...</span>}
        </div>
        <div className="mt-1 text-lg font-semibold text-bmade-950 dark:text-white">
          EUR {pkg.budget_per_package.toFixed(2)}{' '}
          <span className="text-xs font-normal text-bmade-500">
            {pkg.budget_type === 'incl_btw' ? 'incl btw' : 'excl btw'} × {pkg.quantity}
          </span>
        </div>
        <div className="text-xs text-bmade-500">
          = EUR {(pkg.budget_per_package * pkg.quantity).toFixed(0)} totaal
        </div>
      </div>

      <fieldset className="space-y-1.5 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Kostprijs per pakket
        </legend>
        <div className="flex justify-between text-xs">
          <span className="text-bmade-600 dark:text-bmade-400">Recept items</span>
          <span className="font-medium text-bmade-950 dark:text-white">
            EUR {recipeCost.toFixed(2)}
          </span>
        </div>
        <div
          className="flex justify-between text-xs"
          title="Items in colli die over zijn, doorberekend in deze pakketten"
        >
          <span className="text-bmade-600 dark:text-bmade-400">Colli-overhead</span>
          <span
            className={
              overhead.per_package > 0
                ? 'font-medium text-amber-600 dark:text-amber-400'
                : 'text-bmade-500'
            }
          >
            EUR {overhead.per_package.toFixed(2)}
          </span>
        </div>
        <InputRow label="Handling" value={handling} onChange={setHandling} suffix="EUR" />
        <InputRow label="Transport" value={transport} onChange={setTransport} suffix="EUR" />
        <div className="flex justify-between border-t border-bmade-200 pt-2 text-xs font-semibold text-bmade-950 dark:border-bmade-800 dark:text-white">
          <span>Totaal kost / pakket</span>
          <span>EUR {(pricing.total.cost / Math.max(1, pkg.quantity)).toFixed(2)}</span>
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Marge per BTW-categorie
        </legend>

        <MarginSlider
          label="9% BTW (food)"
          value={marginLow}
          onChange={setMarginLow}
          purchaseCost={legacy.low.purchase_cost}
          handlingCost={legacy.low.handling_cost}
          transportCost={legacy.low.transport_cost}
          totalCost={pricing.components.low.cost}
          sellingExcl={pricing.components.low.selling_excl_btw}
          sellingIncl={pricing.components.low.selling_incl_btw}
          marginEuros={pricing.components.low.margin_euros}
          quantity={pkg.quantity}
          accentClass="accent-emerald-500"
          present={hasLow}
        />

        <MarginSlider
          label="21% BTW (non-food)"
          value={marginHigh}
          onChange={setMarginHigh}
          purchaseCost={legacy.high.purchase_cost}
          handlingCost={legacy.high.handling_cost}
          transportCost={legacy.high.transport_cost}
          totalCost={pricing.components.high.cost}
          sellingExcl={pricing.components.high.selling_excl_btw}
          sellingIncl={pricing.components.high.selling_incl_btw}
          marginEuros={pricing.components.high.margin_euros}
          quantity={pkg.quantity}
          accentClass="accent-accent-500"
          present={hasHigh}
        />

        <div className="flex justify-between border-t border-bmade-200 pt-2 text-xs text-bmade-600 dark:border-bmade-800 dark:text-bmade-400">
          <span>Totaal-marge (gewogen)</span>
          <span className="font-semibold text-bmade-950 dark:text-white">
            {pricing.total.weighted_margin_pct.toFixed(1)}%
          </span>
        </div>
        <div className="flex justify-between text-xs text-bmade-600 dark:text-bmade-400">
          <span>Marge totaal in EUR</span>
          <span className="font-medium text-bmade-950 dark:text-white">
            EUR {pricing.total.margin_euros.toFixed(0)}
          </span>
        </div>
      </fieldset>

      <fieldset className="space-y-2 rounded-md border border-accent-300 bg-accent-50/40 p-3 dark:border-accent-500/40 dark:bg-accent-500/5">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-accent-700 dark:text-accent-300">
          Verkoopprijs per pakket
        </legend>
        <div className="flex justify-between text-xs text-bmade-700 dark:text-bmade-300">
          <span>Excl. BTW</span>
          <span>EUR {(pricing.total.selling_excl_btw / Math.max(1, pkg.quantity)).toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-base font-bold text-bmade-950 dark:text-white">
          <span>Incl. BTW</span>
          <span>EUR {sellingPerPkgIncl.toFixed(2)}</span>
        </div>
        <div className="text-[10px] text-bmade-500">
          BTW effectief {effectiveBtwRate.toFixed(1)}% · {pkg.quantity} pakketten = EUR{' '}
          {pricing.total.selling_incl_btw.toFixed(0)} totaal
        </div>
      </fieldset>

      <fieldset className="rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Vergelijk met klant-budget
        </legend>
        {pkg.budget_per_package === 0 ? (
          <p className="text-xs text-bmade-500">Geen klant-budget ingesteld.</p>
        ) : (
          <>
            <BudgetIndicator
              currentIncl={pricing.total.selling_incl_btw}
              targetIncl={customerBudgetTotalIncl}
              perPkgDiff={overUnderPerPkg}
              totalDiff={overUnder}
            />
            {budgetRoomInclPerPkg > 0.01 && (
              <div className="mt-2 space-y-1 border-t border-bmade-200 pt-2 dark:border-bmade-800">
                <div className="flex justify-between text-xs">
                  <span className="text-bmade-600 dark:text-bmade-400">
                    Ruimte na marge (verkoop incl. BTW)
                  </span>
                  <span className="font-medium text-bmade-950 dark:text-white">
                    EUR {budgetRoomInclPerPkg.toFixed(2)} / pakket
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-bmade-600 dark:text-bmade-400">
                    Inkoopprijs-target (excl. marge)
                  </span>
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">
                    food EUR {purchaseTargetFood.toFixed(2)} · non-food EUR{' '}
                    {purchaseTargetNonFood.toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] leading-tight text-bmade-500">
                  Zoek een product met deze inkoopprijs per stuk om het budget precies te vullen.
                </p>
              </div>
            )}
          </>
        )}
      </fieldset>

      <fieldset className="space-y-1 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Inkoopplan voor {pkg.quantity} pakketten
        </legend>
        <div className="flex justify-between text-xs">
          <span className="text-bmade-600 dark:text-bmade-400">Te bestellen totaal</span>
          <span className="font-medium text-bmade-950 dark:text-white">
            EUR {legacy.purchase_cost.toFixed(0)}
          </span>
        </div>
        <div
          className="flex items-center justify-between text-xs"
          title="Wat over blijft van colli, al betaald, herinzetbaar in volgend project"
        >
          <span className="inline-flex items-center gap-1 text-bmade-600 dark:text-bmade-400">
            <Archive className="h-3 w-3" />
            Restvoorraad waarde
          </span>
          <span
            className={
              overhead.leftover_value > 0
                ? 'font-medium text-emerald-600 dark:text-emerald-400'
                : 'text-bmade-500'
            }
          >
            EUR {overhead.leftover_value.toFixed(2)}
          </span>
        </div>
        {overhead.leftover_value > 0 && (
          <p className="pt-1 text-[10px] leading-tight text-bmade-500">
            Al doorberekend in dit project, blijft beschikbaar voor volgend project.
          </p>
        )}
      </fieldset>
    </div>
  );
}

function V2BudgetPanel({ pkg, onChanged }: Props) {
  const [handling, setHandling] = useState(pkg.handling_cost_per_package);
  const [transport, setTransport] = useState(pkg.transport_cost_per_package);
  const [marginLow, setMarginLow] = useState(pkg.margin_low_pct ?? pkg.margin_pct ?? 30);
  const [marginHigh, setMarginHigh] = useState(pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
  const [handlingMargin, setHandlingMargin] = useState(
    pkg.handling_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30
  );
  const [transportMargin, setTransportMargin] = useState(
    pkg.transport_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30
  );
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const requestId = useRef(0);

  useEffect(() => {
    requestId.current += 1;
    setHandling(pkg.handling_cost_per_package);
    setTransport(pkg.transport_cost_per_package);
    setMarginLow(pkg.margin_low_pct ?? pkg.margin_pct ?? 30);
    setMarginHigh(pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
    setHandlingMargin(pkg.handling_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
    setTransportMargin(pkg.transport_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
    setSaveStatus('idle');
  }, [pkg.id]); // Alleen bij pakketwissel; een late response mag lokale invoer niet terugzetten.

  const pricing = useMemo(
    () =>
      calculateGiftPackagePricing({
        ...pkg,
        handling_cost_per_package: handling,
        transport_cost_per_package: transport,
        margin_low_pct: marginLow,
        margin_high_pct: marginHigh,
        handling_margin_pct: handlingMargin,
        transport_margin_pct: transportMargin,
      }),
    [pkg, handling, transport, marginLow, marginHigh, handlingMargin, transportMargin]
  );

  useEffect(() => {
    const changed =
      handling !== pkg.handling_cost_per_package ||
      transport !== pkg.transport_cost_per_package ||
      marginLow !== (pkg.margin_low_pct ?? pkg.margin_pct ?? 30) ||
      marginHigh !== (pkg.margin_high_pct ?? pkg.margin_pct ?? 30) ||
      handlingMargin !== (pkg.handling_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30) ||
      transportMargin !== (pkg.transport_margin_pct ?? pkg.margin_high_pct ?? pkg.margin_pct ?? 30);
    if (!changed) return;

    const currentRequest = ++requestId.current;
    setSaveStatus('saving');
    const timer = window.setTimeout(() => {
      updatePackage(pkg.id, {
        handling_cost_per_package: Math.max(0, handling),
        transport_cost_per_package: Math.max(0, transport),
        margin_low_pct: clampMargin(marginLow),
        margin_high_pct: clampMargin(marginHigh),
        handling_margin_pct: clampMargin(handlingMargin),
        transport_margin_pct: clampMargin(transportMargin),
        margin_pct: Math.round(pricing.total.weighted_margin_pct * 100) / 100,
      })
        .then(async () => {
          if (currentRequest !== requestId.current) return;
          setSaveStatus('saved');
          await onChanged();
        })
        .catch(() => {
          if (currentRequest === requestId.current) setSaveStatus('error');
        });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [
    handling,
    transport,
    marginLow,
    marginHigh,
    handlingMargin,
    transportMargin,
    pkg,
    pricing.total.weighted_margin_pct,
    onChanged,
  ]);

  const perPackage = (value: number) => value / Math.max(1, pkg.quantity);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400">
            Klant-budget
          </div>
          <div className="mt-1 text-lg font-semibold text-bmade-950 dark:text-white">
            EUR {pkg.budget_per_package.toFixed(2)}{' '}
            <span className="text-xs font-normal text-bmade-500">
              {pkg.budget_type === 'incl_btw' ? 'incl btw' : 'excl btw'} × {pkg.quantity}
            </span>
          </div>
        </div>
        <SaveStatus status={saveStatus} />
      </div>

      <fieldset className="space-y-1.5 rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Kostprijs per pakket
        </legend>
        <CostRow label="Receptitems 9%" value={perPackage(pricing.recipe.low)} />
        <CostRow label="Receptitems 21%" value={perPackage(pricing.recipe.high)} />
        <CostRow
          label="Colli-overhead"
          value={perPackage(pricing.overhead.total)}
          title="Colli-overhead zit in de productinkoop en volgt de btw-categorie van het bronartikel."
          warning={pricing.overhead.total > 0}
        />
        <CostInput label="Handling" value={handling} onChange={setHandling} />
        <CostInput label="Transport" value={transport} onChange={setTransport} />
        <CostRow label="Totale kostprijs" value={perPackage(pricing.total.cost)} strong />
      </fieldset>

      <fieldset className="rounded-md border border-bmade-200 p-3 dark:border-bmade-800">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-bmade-500">
          Brutomarge per onderdeel
        </legend>
        <div className="grid gap-3 lg:grid-cols-2">
          <PricingComponentCard
            title="9% btw — artikelen"
            component={pricing.components.low}
            value={marginLow}
            onChange={setMarginLow}
            disabled={pricing.components.low.cost === 0}
            quantity={pkg.quantity}
          />
          <PricingComponentCard
            title="21% btw — artikelen"
            component={pricing.components.high}
            value={marginHigh}
            onChange={setMarginHigh}
            disabled={pricing.components.high.cost === 0}
            quantity={pkg.quantity}
          />
          <PricingComponentCard
            title="Handling — 21% btw"
            component={pricing.components.handling}
            value={handlingMargin}
            onChange={setHandlingMargin}
            disabled={pricing.components.handling.cost === 0}
            quantity={pkg.quantity}
          />
          <PricingComponentCard
            title="Transport — 21% btw"
            component={pricing.components.transport}
            value={transportMargin}
            onChange={setTransportMargin}
            disabled={pricing.components.transport.cost === 0}
            quantity={pkg.quantity}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-1 rounded-md border border-accent-300 bg-accent-50/40 p-3 text-xs dark:border-accent-500/40 dark:bg-accent-500/5">
        <legend className="px-1 text-[10px] uppercase tracking-wider text-accent-700 dark:text-accent-300">
          Eindtotaal
        </legend>
        <TotalRow label="Totale kostprijs" value={pricing.total.cost} />
        <TotalRow label="Totale brutowinst" value={pricing.total.margin_euros} />
        <div className="flex justify-between"><span>Gewogen brutomarge</span><strong>{pricing.total.weighted_margin_pct.toFixed(1)}%</strong></div>
        <TotalRow label="Verkoop excl. btw" value={pricing.total.selling_excl_btw} />
        <TotalRow label="Btw" value={pricing.total.btw_euros} />
        <TotalRow label="Verkoop incl. btw totaal" value={pricing.total.selling_incl_btw} strong />
      </fieldset>
    </div>
  );
}

function clampMargin(value: number): number {
  return Math.min(70, Math.max(0, Number.isFinite(value) ? value : 0));
}

function SaveStatus({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  const labels = { idle: '', saving: 'opslaan...', saved: 'opgeslagen', error: 'opslaan mislukt' };
  return <span aria-live="polite" className={status === 'error' ? 'text-xs text-red-600' : 'text-xs text-bmade-500'}>{labels[status]}</span>;
}

function CostRow({ label, value, title, warning = false, strong = false }: { label: string; value: number; title?: string; warning?: boolean; strong?: boolean }) {
  return <div className={`flex justify-between gap-3 text-xs ${strong ? 'border-t border-bmade-200 pt-2 font-semibold dark:border-bmade-800' : ''}`} title={title}><span className="text-bmade-600 dark:text-bmade-400">{label}</span><span className={warning ? 'font-medium text-amber-600 dark:text-amber-400' : 'font-medium text-bmade-950 dark:text-white'}>EUR {value.toFixed(2)}</span></div>;
}

function CostInput({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <label className="flex items-center justify-between gap-3 text-xs text-bmade-600 dark:text-bmade-400"><span>{label}</span><span className="flex items-center gap-1"><input aria-label={label} type="number" min="0" step="0.1" value={value} onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))} className="w-20 rounded border border-bmade-200 bg-white px-2 py-1 text-right text-sm text-bmade-950 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white" /> EUR</span></label>;
}

function TotalRow({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return <div className={`flex justify-between ${strong ? 'border-t border-accent-200 pt-2 text-sm font-bold dark:border-accent-500/30' : ''}`}><span>{label}</span><span>EUR {value.toFixed(2)}</span></div>;
}

function MarginSlider({
  label,
  value,
  onChange,
  purchaseCost,
  handlingCost,
  transportCost,
  totalCost,
  sellingExcl,
  sellingIncl,
  marginEuros,
  quantity,
  accentClass,
  present,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  purchaseCost: number;
  handlingCost: number;
  transportCost: number;
  totalCost: number;
  sellingExcl: number;
  sellingIncl: number;
  marginEuros: number;
  quantity: number;
  accentClass: string;
  present: boolean;
}) {
  return (
    <div className={present ? '' : 'opacity-50'}>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-medium text-bmade-700 dark:text-bmade-300">
          {label}
          {!present && (
            <span className="ml-2 text-[10px] font-normal text-bmade-400">geen items</span>
          )}
        </label>
        <div className="w-12 text-right text-sm font-semibold text-bmade-950 dark:text-white">
          {value.toFixed(0)}%
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={70}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`mt-1 w-full ${accentClass}`}
        disabled={!present}
      />
      {present && (
        <div className="mt-2 space-y-1 border-t border-bmade-100 pt-2 text-[10px] dark:border-bmade-800">
          <BreakdownRow label="Productinkoop" value={purchaseCost} />
          <BreakdownRow label="Handling" value={handlingCost} />
          <BreakdownRow label="Transport" value={transportCost} />
          <BreakdownRow label="Totale kostprijs" value={totalCost} emphasized />
          <BreakdownRow label="Brutowinst" value={marginEuros} />
          <BreakdownRow label="Verkoop excl. btw" value={sellingExcl} emphasized />
          <BreakdownRow label="Verkoop incl. btw" value={sellingIncl} />
        </div>
      )}
      {present && quantity > 0 && (
        <div className="text-[10px] text-bmade-500">
          Marge EUR {marginEuros.toFixed(0)} totaal · EUR {(marginEuros / quantity).toFixed(2)} per
          pakket
        </div>
      )}
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: number;
  emphasized?: boolean;
}) {
  return (
    <div
      className={`flex justify-between gap-3 ${
        emphasized
          ? 'font-semibold text-bmade-950 dark:text-white'
          : 'text-bmade-500 dark:text-bmade-400'
      }`}
    >
      <span>{label}</span>
      <span>EUR {value.toFixed(0)}</span>
    </div>
  );
}

function InputRow({
  label,
  value,
  onChange,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <label className="text-bmade-600 dark:text-bmade-400">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          step={0.1}
          min={0}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-20 rounded border border-bmade-200 bg-white px-2 py-1 text-right text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white"
        />
        {suffix && <span className="text-bmade-500">{suffix}</span>}
      </div>
    </div>
  );
}

function BudgetIndicator({
  currentIncl,
  targetIncl,
  perPkgDiff,
  totalDiff,
}: {
  currentIncl: number;
  targetIncl: number;
  perPkgDiff: number;
  totalDiff: number;
}) {
  const ratio = targetIncl > 0 ? currentIncl / targetIncl : 0;
  const pct = Math.min(150, ratio * 100);
  const isOver = totalDiff > 0.5;
  const isUnder = totalDiff < -0.5;
  const Icon = isOver ? TrendingUp : TrendingDown;
  const tone = isOver
    ? 'text-red-600 dark:text-red-400'
    : isUnder
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-bmade-600 dark:text-bmade-400';
  const barColor = isOver ? 'bg-red-500' : isUnder ? 'bg-emerald-500' : 'bg-accent-500';

  return (
    <div className="space-y-2">
      <div className="h-2 w-full overflow-hidden rounded-full bg-bmade-100 dark:bg-bmade-800">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`flex items-center justify-between text-xs ${tone}`}>
        <span className="inline-flex items-center gap-1">
          <Icon className="h-3 w-3" />
          {isOver
            ? `EUR ${Math.abs(perPkgDiff).toFixed(2)} over per pakket`
            : isUnder
              ? `EUR ${Math.abs(perPkgDiff).toFixed(2)} ruimte per pakket`
              : 'precies binnen budget'}
        </span>
        <span className="font-semibold">
          {isOver ? '+' : ''}
          EUR {totalDiff.toFixed(0)} totaal
        </span>
      </div>
    </div>
  );
}
